import {ProjectMinRes} from '../tracker/project-tracker';
import {Type} from 'class-transformer';
import {
    ColumnType,
    FileUploadRes,
    yascTableIgnore,
    yascTableIndex, yascTableWidth,
    yascAutocomplete,
    yascFormIgnore,
    yascFormIndex,
    yascMaxLength,
    yascMin,
    yascMinLength,
    yascOptionsKey,
    yascRequired,
    yascTableLevelOfDetail,
    yascDisplayName,
    yascTableDisplayFn,
    yascTableColumn,
    yascRequestOverride,
    yascFormGrid,
    yascClassFormSectionTitle,
    yascEntityName,
} from '@yasc/yasc-material-components';
import {ContactPersonDetailRes} from '../user.contract';
import {formatFromMinutes, minToHours} from './general-task-utils';
import {TaskEffortRes, TaskEffortUpdateReq} from './task-effort';
import {
    yascFormKind,
    yascFormRows
} from '@yasc/yasc-material-components';

@yascClassFormSectionTitle('crm_task_details_sectionTitle')
class baseTask {
    @yascDisplayName('general_name')
    @yascTableIndex(0)
    @yascTableLevelOfDetail(0)
    @yascFormGrid({row: 0, width: 3})
    @yascRequired()
    @yascMinLength(1)
    @yascMaxLength(255)
    @yascFormIndex(0)
    name!: string;

    @yascDisplayName('crm_task_taskId_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    taskID?: string;

    @yascDisplayName('crm_contract_category_label')
    @yascTableIndex(2)
    @yascTableLevelOfDetail(1)
    @yascRequired()
    @yascMinLength(1)
    @yascMaxLength(255)
    @yascFormIndex(1)
    @yascAutocomplete()
    @yascOptionsKey('categories')
    category!: string;

    @yascDisplayName('crm_task_mockup_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    mockups: FileUploadRes[] = [];

    @yascDisplayName('crm_contract_taskRisks_label')
    @yascTableColumn(ColumnType.Tag)
    @yascTableDisplayFn((val: string[]) => val.join(', '))
    @yascTableLevelOfDetail(1)
    @yascFormIgnore()
    risks: string[] = [];

    @yascDisplayName('crm_contract_taskClarifications_label')
    @yascTableIgnore(true)
    @yascTableColumn(ColumnType.Tag)
    @yascTableDisplayFn((val: string[]) => val.join(', '))
    @yascTableLevelOfDetail(1)
    @yascFormIgnore()
    clarifications: string[] = [];

    @yascDisplayName('crm_contract_taskBudget_label')
    @yascTableColumn(ColumnType.Number)
    @yascTableLevelOfDetail(1)
    @yascMin(0)
    @yascFormIgnore()
    maxBudget!: number;

    @yascDisplayName('crm_task_currentPlanned_label')
    @yascTableColumn(ColumnType.Number)
    @yascTableLevelOfDetail(2)
    @yascFormIgnore()
    currentPlanned!: number;

    @yascDisplayName('general_description')
    @yascTableLevelOfDetail(2)
    @yascFormKind('textarea')
    @yascFormRows(4)
    @yascFormGrid({fullRow: true})
    @yascFormIndex(10)
    description?: string;

    @yascDisplayName('crm_task_recentAppointments_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    recentAppointmentsCnt!: number;

    constructor(value?: Partial<baseTask>) {
        Object.assign(this, value)
    }
}

// @yascClassDisplayName('crm_contract_baseTaskClass_title')
// @ClassFormSectionTitle('crm_task_details_sectionTitle')
@yascEntityName('crm_task_entityName')
export class BaseTaskRes {
    @yascDisplayName('crm_task_id_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    id!: string;

    @yascDisplayName('crm_task_taskId_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    taskId!: string;

    @yascDisplayName('crm_contract_category_label')
    @yascTableIndex(2)
    @yascTableColumn(ColumnType.Text)
    @yascTableLevelOfDetail(1)
    @yascRequired()
    @yascTableIgnore(true)
    @yascMinLength(1)
    @yascMaxLength(255)
    @yascFormIndex(1)
    category!: string;

    @yascDisplayName('crm_task_mockup_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    mockup: string[] = [];

    @yascDisplayName('crm_task_contact_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    contact: ContactPersonDetailRes[] = [];

    @yascDisplayName('crm_task_references_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    references: BaseTaskRes[] = [];

    @yascDisplayName('crm_contract_taskRisks_label')
    @yascTableColumn(ColumnType.Tag)
    @yascTableDisplayFn((val: string[]) => val.join(', '))
    @yascTableIgnore(true)
    @yascTableLevelOfDetail(1)
    @yascFormIgnore()
    risks: string[] = [];

    @yascDisplayName('crm_contract_taskClarifications_label')
    @yascTableIgnore(true)
    @yascTableColumn(ColumnType.Tag)
    @yascTableDisplayFn((val: string[]) => val.join(', '))
    @yascTableLevelOfDetail(1)
    @yascFormIgnore()
    clarifications: string[] = [];

    @yascDisplayName('crm_task_originalStart_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    originalStart!: string;

    @yascDisplayName('crm_task_originalEnd_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    originalEnd!: string;

    @yascDisplayName('crm_task_originalEstimated_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    originalEstimated!: number;

    @yascDisplayName('crm_task_currentEst_label')
    @yascTableColumn(ColumnType.Number)
    @yascTableIgnore(true)
    @yascTableLevelOfDetail(2)
    @yascMin(0)
    @yascFormIndex(2)
    currentEstimated!: number;

    @yascDisplayName('crm_task_currentStart_label')
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(1)
    @yascTableIgnore(true)
    @yascRequired()
    @yascFormIndex(3)
    currentStart!: Date;

    @yascDisplayName('crm_task_currentEnd_label')
    @yascTableIgnore(true)
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(1)
    @yascRequired()
    @yascFormIndex(4)
    currentEnd!: Date;


    @yascDisplayName('crm_task_currentPlanned_label')
    @yascTableColumn(ColumnType.Number)
    @yascTableLevelOfDetail(2)
    @yascTableIgnore(true)
    @yascFormIgnore()
    currentPlanned!: number;

    @yascDisplayName('crm_task_taskType_label')
    @yascTableColumn(ColumnType.Text)
    @yascTableLevelOfDetail(1)
    @yascTableIgnore(true)
    @yascFormIgnore()
    taskType!: string;

    @yascDisplayName('general_name')
    @yascTableIndex(0)
    @yascTableLevelOfDetail(0)
    @yascTableWidth(3)
    @yascRequired()
    @yascMinLength(1)
    @yascMaxLength(255)
    @yascFormIndex(0)
    name?: string;

    @yascDisplayName('general_description')
    @yascTableLevelOfDetail(2)
    @yascFormKind('textarea')
    @yascFormRows(4)
    @yascFormGrid({fullRow: true})
    @yascFormIndex(10)
    description?: string;

    @yascDisplayName('crm_task_recentAppointments_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    recentAppointmentsCnt!: number;

    @Type(() => ProjectMinRes)
    @yascDisplayName('general_project')
    @yascRequestOverride('projectId')
    @yascTableDisplayFn((val: ProjectMinRes) => val === undefined ? 'No Project Assigned' : val.name)
    @yascTableLevelOfDetail(3)
    @yascFormIndex(5)
    project?: ProjectMinRes;

    @Type(() => ContactPersonDetailRes)
    @yascDisplayName('crm_contract_connectedContacts_label')
    @yascTableDisplayFn((val: ContactPersonDetailRes[]) => val.map(x => x.firstName.charAt(0) + '. ' + x.lastName).join(', '))
    @yascTableLevelOfDetail(2)
    @yascFormIgnore()
    contacts: ContactPersonDetailRes[] = [];

    @yascDisplayName('crm_contract_taskCurrentStart_label')
    @yascFormGrid({row: 3, width: 2})
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(2)
    @yascFormIgnore()
    get currentStartDate(): Date {
        return new Date(this.currentStart);
    }

    set currentStartDate(date: Date) {
        this.currentEnd = date
    }

    @yascDisplayName('crm_contract_taskCurrentEnd_label')
    @yascFormGrid({row: 3, width: 2})
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(2)
    @yascFormIgnore()
    get currentEndDate(): Date {
        return new Date(this.currentEnd)
    }

    set currentEndDate(date: Date) {
        this.currentEnd = date
    }

    @yascDisplayName('crm_contract_referencedTasks_label')
    @yascTableIgnore(true)
    @yascFormGrid({row: 5, width: 3})
    @yascTableColumn(ColumnType.Select)
    @yascRequestOverride('referencedTaskIds')
    @Type(() => BaseTaskRes)
    @yascTableLevelOfDetail(2)
    @yascFormIgnore()
    referencedTasks: BaseTaskRes[] = [];

    @yascDisplayName('crm_contract_referencedByTasks_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    @yascTableColumn(ColumnType.Select)
    @yascRequestOverride('referencedByTaskIds')
    @Type(() => BaseTaskRes)
    @yascTableLevelOfDetail(2)
    referencedByTask: BaseTaskRes[] = [];

    @yascDisplayName('crm_contract_taskEffort_label')
    @Type(() => TaskEffortRes)
    @yascTableDisplayFn((val: TaskEffortRes) => val.name)
    @yascTableLevelOfDetail(2)
    @yascFormIgnore()
    efforts: TaskEffortRes[] = [];

    @yascDisplayName('crm_contract_customerFacingId_label')
    @yascTableIndex(1)
    @yascTableLevelOfDetail(1)
    @yascFormIgnore()
    @yascTableIgnore(true)
    get customerFacingId(): string {
        if (this.taskId !== undefined) return this.taskId;
        return this.id.toString()
    }

    @yascDisplayName('crm_contract_taskEstimatedEffortSum_label')
    @yascFormIgnore()
    @yascTableDisplayFn((val) => val.formatted)
    @yascTableLevelOfDetail(2)
    get estimatedEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.estimatedDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }

    @yascDisplayName('crm_contract_taskActualEffortSum_label')
    @yascTableDisplayFn((val) => val.formatted)
    @yascFormIgnore()
    @yascTableLevelOfDetail(2)
    get actualEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.actualDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }

    private toUpdateCreate(value: Partial<BaseTaskRes>): BaseTaskCreateReq {
        let create = new BaseTaskCreateReq({})
        create.start = value.currentStart!
        create.end = value.currentEnd!
        create.estimated = value.currentEstimated ?? 0
        create.contactIds = value.contacts?.map(x => x.id) ?? []
        create.referencedTaskIds = value.referencedTasks?.map(x => x.id) ?? []
        create.referencedByTaskIds = value.referencedByTask?.map(x => x.id) ?? []
        create.projectId = value.project?.id
        return create
    }

    public toCreate(): BaseTaskCreateReq {
        return this.toUpdateCreate(this)
    }

    public toUpdate(): BaseTaskUpdateReq {
        let update = this.toUpdateCreate(this) as BaseTaskUpdateReq;
        return update
    }
}

export class BaseTaskCreateReq extends baseTask {
    @yascDisplayName('general_project')
    @yascTableIgnore(true)
    @yascAutocomplete()
    @yascOptionsKey('projects')
    @yascFormIndex(5)
    projectId?: number;

    @yascDisplayName('crm_contract_connectedContacts_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    contactIds: number[] = [];

    @yascDisplayName('crm_contract_referencedTasks_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    referencedTaskIds: string[] = [];

    @yascDisplayName('crm_contract_referencedByTasks_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    referencedByTaskIds: string[] = [];

    @yascDisplayName('crm_task_currentEst_label')
    @yascTableColumn(ColumnType.Number)
    @yascTableLevelOfDetail(2)
    @yascMin(0)
    @yascFormIndex(4)
    estimated!: number;

    @yascDisplayName('crm_task_currentStart_label')
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(1)
    @yascRequired()
    @yascFormIndex(2)
    start!: Date;

    @yascDisplayName('crm_task_currentEnd_label')
    @yascTableIgnore(true)
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(1)
    @yascRequired()
    @yascFormIndex(3)
    end!: Date;

    constructor(value: Partial<BaseTaskCreateReq>) {
        super(value);
        Object.assign(this, value)
    }
}

export class BaseTaskUpdateReq extends BaseTaskCreateReq {
    @yascDisplayName('crm_task_id_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    id!: string;

    @yascDisplayName('crm_contract_taskEffort_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    efforts: TaskEffortUpdateReq[] = [];

    constructor(value: Partial<BaseTaskUpdateReq>) {
        super(value);
        Object.assign(this, value)
    }

    @yascDisplayName('crm_contract_taskEstimatedEffortSum_label')
    @yascFormIgnore()
    @yascTableDisplayFn((val) => val.formatted)
    @yascTableLevelOfDetail(1)
    get estimatedEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.estimatedDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }

    @yascDisplayName('crm_contract_taskActualEffortSum_label')
    @yascTableDisplayFn((val) => val.formatted)
    @yascFormIgnore()
    @yascTableLevelOfDetail(1)
    get actualEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.actualDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }
}
